import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { T, US_STATES } from "../tokens";

const defaultSettings = {
  defaultSurveyName: "",
  defaultSurveyorName: "",
  surveyType: "pollard",
  transectDivision: "habitat",
  location: "IA",
  temperatureUnit: "F",
  sectionType: "alpha",
  numSections: 5,
  windType: "numeric",
  cloudType: "numeric",
  viewingRadius: 10,
  transectDistanceMeasurement: 0,
};

// ─── Icons ─────────────────────────────────────────────────────────────
const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const UserIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const PinIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M12 22s7-7 7-12a7 7 0 10-14 0c0 5 7 12 7 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

// ─── Shared atoms ─────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.ink2, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '20px 4px 10px' }}>
      {children}
    </div>
  );
}

function FieldWrap({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      {label && (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <label style={{ fontFamily: T.font, fontSize: 14, fontWeight: 600, color: T.ink, letterSpacing: '0.01em' }}>{label}</label>
          {hint && <span style={{ fontFamily: T.font, fontSize: 12, color: T.ink3 }}>{hint}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, leadingIcon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`, height: 54, paddingLeft: leadingIcon ? 14 : 16 }}>
      {leadingIcon && <span style={{ color: T.ink3, marginRight: 10, display: 'flex' }}>{leadingIcon}</span>}
      <input type="text" value={value} onChange={onChange} placeholder={placeholder} style={{
        flex: 1, height: '100%', border: 'none', outline: 'none', background: 'transparent',
        fontFamily: T.font, fontSize: 17, color: T.ink, padding: '0 16px 0 0', letterSpacing: '-0.01em', minWidth: 0,
      }} />
    </div>
  );
}

function SelectInput({ value, onChange, children, leadingIcon }) {
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`, height: 54, paddingLeft: leadingIcon ? 14 : 16, paddingRight: 14 }}>
      {leadingIcon && <span style={{ color: T.ink3, marginRight: 10, display: 'flex' }}>{leadingIcon}</span>}
      <select value={value} onChange={onChange} style={{
        flex: 1, height: '100%', border: 'none', outline: 'none', background: 'transparent',
        fontFamily: T.font, fontSize: 17, color: T.ink, appearance: 'none', WebkitAppearance: 'none',
        letterSpacing: '-0.01em', cursor: 'pointer', minWidth: 0,
      }}>{children}</select>
      <span style={{ color: T.ink3, pointerEvents: 'none', display: 'flex' }}>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', background: T.bgDeep, borderRadius: T.r.md, padding: 4, gap: 4 }}>
      {options.map(o => {
        const active = o.value === value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{
            flex: 1, height: 44, border: 'none',
            background: active ? T.card : 'transparent',
            color: active ? T.ink : T.ink2,
            fontFamily: T.font, fontSize: 15, fontWeight: active ? 600 : 500,
            borderRadius: T.r.sm, cursor: 'pointer',
            boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
            letterSpacing: '-0.01em',
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

function Stepper({ value, onChange, min = 0, max = 99 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} style={{
        width: 64, height: 64, border: `1px solid ${T.line}`, background: value <= min ? T.bgDeep : T.card,
        color: value <= min ? T.ink3 : T.ink, cursor: value <= min ? 'default' : 'pointer',
        borderRadius: T.r.md, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none"><path d="M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
      </button>
      <div style={{ flex: 1, height: 64, background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.fontDisplay, fontSize: 26, fontWeight: 600, color: T.ink, letterSpacing: '-0.02em' }}>{value}</div>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} style={{
        width: 64, height: 64, border: `1px solid ${T.line}`, background: value >= max ? T.bgDeep : T.card,
        color: value >= max ? T.ink3 : T.ink, cursor: value >= max ? 'default' : 'pointer',
        borderRadius: T.r.md, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
}

export default function SettingsPageComponent() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(defaultSettings);

  const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.userId) {
          localStorage.clear();
          navigate("/");
        };
        const response = await fetch(`api/users/${user.userId}/settings`);
        if (!response.ok) throw new Error("No settings found");
        const data = await response.json();
        setSettings({ ...defaultSettings, ...data });
      } catch (error) {
        console.error("Error loading settings:", error);
        const saved = localStorage.getItem("surveySettings");
        if (saved) setSettings(JSON.parse(saved));
      }
    };
    fetchSettings();
  }, []);

  const saveSettings = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const response = await fetch(`api/users/${user?.userId}/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!response.ok) throw new Error("Failed to save settings");
      await response.json();
      alert("Settings saved!");
    } catch (error) {
      console.error(error);
      alert("Error saving settings");
    }
  };

  const card = (children) => (
    <div style={{ background: T.card, borderRadius: T.r.lg, border: `1px solid ${T.line}`, padding: 16 }}>
      {children}
    </div>
  );

  return (
    <div className="lep-page">
      <div className="lep-page-inner">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '54px 16px 12px', gap: 8, background: T.bg, position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => navigate('/menu')} style={{ width: 40, height: 40, border: 'none', background: 'transparent', borderRadius: T.r.pill, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.ink, cursor: 'pointer' }}>
            <ArrowLeft />
          </button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 600, color: T.ink, letterSpacing: '-0.01em' }}>Settings</div>
          </div>
          <div style={{ width: 40 }} />
        </div>

        <div style={{ padding: '0 20px', paddingBottom: 120 }}>
          <SectionLabel>Defaults</SectionLabel>
          {card(<>
            <FieldWrap label="Default survey name">
              <TextInput value={settings.defaultSurveyName} onChange={e => set('defaultSurveyName', e.target.value)} placeholder="e.g. Neal Smith Refuge" />
            </FieldWrap>
            <FieldWrap label="Default surveyor">
              <TextInput value={settings.defaultSurveyorName} onChange={e => set('defaultSurveyorName', e.target.value)} placeholder="Name" leadingIcon={<UserIcon />} />
            </FieldWrap>
            <FieldWrap label="Survey type">
              <Segmented value={settings.surveyType} onChange={v => set('surveyType', v)}
                options={[{ value: 'pollard', label: 'Pollard' }, { value: 'meandering', label: 'Meandering' }, { value: 'incidental', label: 'Incidental' }]} />
            </FieldWrap>
            <FieldWrap label="Default location" hint="Filters species list">
              <SelectInput value={settings.location} onChange={e => set('location', e.target.value)} leadingIcon={<PinIcon />}>
                {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </SelectInput>
            </FieldWrap>
          </>)}

          <SectionLabel>Pollard sections</SectionLabel>
          {card(<>
            <FieldWrap label="Number of sections" hint={`A through ${String.fromCharCode(64 + Number(settings.numSections))}`}>
              <Stepper value={Number(settings.numSections)} onChange={v => set('numSections', v)} min={1} max={15} />
            </FieldWrap>
          </>)}

          <SectionLabel>Units</SectionLabel>
          {card(<>
            <FieldWrap label="Temperature">
              <Segmented value={settings.temperatureUnit} onChange={v => set('temperatureUnit', v)}
                options={[{ value: 'F', label: '°Fahrenheit' }, { value: 'C', label: '°Celsius' }]} />
            </FieldWrap>
            <FieldWrap label="Wind speed">
              <Segmented value={settings.windType} onChange={v => set('windType', v)}
                options={[{ value: 'mph', label: 'mph' }, { value: 'kph', label: 'kph' }]} />
            </FieldWrap>
          </>)}
        </div>

        {/* Sticky save */}
        <div className="lep-sticky-footer">
          <button onClick={saveSettings} style={{
            width: '100%', height: 56, border: 'none',
            background: T.forest, color: '#fff',
            borderRadius: T.r.md, fontFamily: T.font, fontSize: 17, fontWeight: 600,
            letterSpacing: '-0.01em', cursor: 'pointer',
            boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 4px 14px rgba(45,80,22,0.18)',
          }}>
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
